import useNotificationPreferences from "api/notificationResource/useNotificationPreferences";
import useUpdateNotificationPreferences from "api/notificationResource/useUpdateNotificationPreferences";
import useGenres from "api/GenreResource/useGenres";
import useProfile from "api/UserResource/useProfile";
import AccountLayout from "../AccountLayout";
import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import { FiBell } from "react-icons/fi";
import { useState, useEffect } from "react";

const NotificationPreferences = () => {
	const { data: profile, isLoading: isLoadingProfile } = useProfile();
	const { data: prefs, isLoading: isLoadingPrefs } = useNotificationPreferences();
	const { data: genresRes } = useGenres("yes");
	const { mutateAsync: updatePrefs, isLoading: isUpdating } = useUpdateNotificationPreferences();

	const [priceDropAlerts, setPriceDropAlerts] = useState(true);
	const [newBookAlerts, setNewBookAlerts] = useState(true);
	const [readingReminders, setReadingReminders] = useState(true);
	const [orderStatusUpdates, setOrderStatusUpdates] = useState(true);
	const [alertGenres, setAlertGenres] = useState([]);

	useEffect(() => {
		if (prefs) {
			setPriceDropAlerts(prefs.price_drop_alerts ?? true);
			setNewBookAlerts(prefs.new_book_alerts ?? true);
			setReadingReminders(prefs.reading_reminders ?? true);
			setOrderStatusUpdates(prefs.order_status_updates ?? true);
			setAlertGenres(Array.isArray(prefs.alert_genres) ? prefs.alert_genres : []);
		}
	}, [prefs]);

	const genres = genresRes?.data?.data ?? [];

	const handleToggle = (key, value) => {
		const payload = { [key]: value };
		updatePrefs(payload);
		switch (key) {
			case "price_drop_alerts":
				setPriceDropAlerts(value);
				break;
			case "new_book_alerts":
				setNewBookAlerts(value);
				break;
			case "reading_reminders":
				setReadingReminders(value);
				break;
			case "order_status_updates":
				setOrderStatusUpdates(value);
				break;
			default:
				break;
		}
	};

	const handleGenreToggle = (genreId) => {
		const next = alertGenres.includes(genreId)
			? alertGenres.filter((id) => id !== genreId)
			: [...alertGenres, genreId];
		setAlertGenres(next);
		updatePrefs({ alert_genres: next });
	};

	const ToggleRow = ({ label, description, checked, onChange }) => (
		<div className="flex items-start justify-between gap-4 py-4 border-b border-gray-100 last:border-0">
			<div className="flex-1">
				<p className="font-medium text-gray-900">{label}</p>
				<p className="text-sm text-gray-500 mt-0.5">{description}</p>
			</div>
			<button
				type="button"
				role="switch"
				aria-checked={checked}
				onClick={() => onChange(!checked)}
				disabled={isUpdating}
				className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2 disabled:opacity-50 ${
					checked ? "bg-akgreen" : "bg-gray-200"
				}`}
			>
				<span
					className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
						checked ? "translate-x-5" : "translate-x-1"
					}`}
				/>
			</button>
		</div>
	);

	return (
		<Container>
			<div className="min-h-screen bg-gray-50">
				<div className="pt-8 pb-16">
					<PaddingX>
						<AccountLayout isLoading={isLoadingProfile} profile={profile?.data}>
							<div className="mb-10">
								<h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
									<FiBell className="w-7 h-7 text-akgreen" />
									Notification preferences
								</h1>
								<p className="mt-1 text-sm text-gray-500">
									Choose which emails and notifications you want to receive.
								</p>
							</div>

							{isLoadingPrefs ? (
								<div className="flex justify-center py-16">
									<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-akgreen" />
								</div>
							) : (
								<div className="space-y-6">
									<div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
										<div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
											<h2 className="font-semibold text-gray-900">Email notifications</h2>
										</div>
										<div className="px-6 divide-y divide-gray-100">
											<ToggleRow
												label="Price drop alerts"
												description="Get notified when books in your wishlist go on sale"
												checked={priceDropAlerts}
												onChange={(v) => handleToggle("price_drop_alerts", v)}
											/>
											<ToggleRow
												label="New book alerts by genre"
												description="Receive emails when new books in your selected genres are added"
												checked={newBookAlerts}
												onChange={(v) => handleToggle("new_book_alerts", v)}
											/>
											<ToggleRow
												label="Reading reminders"
												description="Gentle reminders when you haven't read in a while"
												checked={readingReminders}
												onChange={(v) => handleToggle("reading_reminders", v)}
											/>
											<ToggleRow
												label="Order status updates"
												description="Updates when your order is assigned, in transit, or delivered"
												checked={orderStatusUpdates}
												onChange={(v) => handleToggle("order_status_updates", v)}
											/>
										</div>
									</div>

									{newBookAlerts && (
										<div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
											<div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
												<h2 className="font-semibold text-gray-900">Alert genres</h2>
												<p className="text-sm text-gray-500 mt-0.5">
													Select genres you want to be notified about when new books are added
												</p>
											</div>
											<div className="px-6 py-4">
												<div className="flex flex-wrap gap-2">
													{genres.map((g) => (
														<label
															key={g.id}
															className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50"
															style={{
																borderColor: alertGenres.includes(g.id)
																	? "var(--akgreen, #22c55e)"
																	: "#e5e7eb",
																backgroundColor: alertGenres.includes(g.id)
																	? "rgba(34, 197, 94, 0.1)"
																	: "transparent",
															}}
														>
															<input
																type="checkbox"
																checked={alertGenres.includes(g.id)}
																onChange={() => handleGenreToggle(g.id)}
																className="sr-only"
															/>
															<span className="text-sm font-medium text-gray-700">
																{g.name}
															</span>
														</label>
													))}
												</div>
												{genres.length === 0 && (
													<p className="text-sm text-gray-500">No genres available</p>
												)}
											</div>
										</div>
									)}
								</div>
							)}
						</AccountLayout>
					</PaddingX>
				</div>
			</div>
		</Container>
	);
};

export default NotificationPreferences;
